// Static API service that uses local JSON data instead of backend API
// This is for the MVP deployment that skips the backend

let restaurantData = null;

// Load the static JSON data
const loadRestaurantData = async () => {
  if (restaurantData) return restaurantData;
  
  try {
    const response = await fetch('/restaurants-data.json');
    restaurantData = await response.json();
    return restaurantData;
  } catch (error) {
    console.error('Failed to load restaurant data:', error);
    // Fallback to empty data structure
    return {
      restaurants: [],
      cities: [],
      certifications: [],
      total_restaurants: 0
    };
  }
};

// Transform scraped data into the expected API format
const transformRestaurantData = (scraped) => {
  return scraped.restaurants.map((restaurant, index) => ({
    id: index + 1,
    name: restaurant.name,
    description: restaurant.description,
    street_address: restaurant.address?.split(',')[0] || restaurant.address,
    city: restaurant.city,
    state: restaurant.state,
    zip_code: restaurant.zip_code || '',
    phone: restaurant.phone,
    website: restaurant.website || '',
    email: restaurant.email || '',
    cuisine_types: restaurant.cuisine_type ? [restaurant.cuisine_type] : [],
    price_range: restaurant.price_range || 2,
    hours: {
      regular: restaurant.hours || 'Contact for hours',
      shabbos_hours: null
    },
    certifications: restaurant.certification ? [{
      abbreviation: restaurant.certification,
      name: getCertificationName(restaurant.certification)
    }] : [],
    dietary_requirements: [],
    delivery_options: [],
    rating: restaurant.rating || null,
    review_count: 0,
    created_at: restaurant.scraped_at,
    updated_at: restaurant.scraped_at,
    verified: false,
    image_url: null,
    latitude: null,
    longitude: null
  }));
};

const getCertificationName = (abbr) => {
  const certNames = {
    'OU': 'Orthodox Union',
    'OK': 'Organized Kashrus',
    'Star-K': 'Star-K Kosher Certification',
    'CRC': 'Chicago Rabbinical Council',
    'KOF-K': 'Kosher Supervision of America'
  };
  return certNames[abbr] || abbr;
};

// Static API endpoints that mimic the real API
export const searchAPI = {
  search: async (params) => {
    const data = await loadRestaurantData();
    const restaurants = transformRestaurantData(data);
    
    let filtered = restaurants;
    
    // Enhanced fuzzy search with scoring
    if (params.q && params.q.trim()) {
      const query = params.q.toLowerCase().trim();
      const searchTerms = query.split(/\s+/);
      
      filtered = restaurants.map(r => {
        let totalScore = 0;
        let matchCount = 0;
        
        const searchableFields = [
          { field: r.name, weight: 5 },
          { field: r.city, weight: 3 },
          { field: r.state, weight: 2 },
          { field: r.cuisine_types.join(' '), weight: 4 },
          { field: r.certifications.map(c => c.abbreviation + ' ' + c.name).join(' '), weight: 3 },
          { field: r.description || '', weight: 1 },
          { field: r.street_address || '', weight: 1 }
        ];
        
        searchTerms.forEach(term => {
          searchableFields.forEach(({ field, weight }) => {
            if (field && field.toLowerCase().includes(term)) {
              const fieldLower = field.toLowerCase();
              let score = 0;
              
              // Exact match
              if (fieldLower === term) score = 100;
              // Starts with
              else if (fieldLower.startsWith(term)) score = 80;
              // Word boundary match
              else if (fieldLower.match(new RegExp(`\\b${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i'))) score = 60;
              // Contains
              else if (fieldLower.includes(term)) score = 40;
              
              totalScore += score * weight;
              if (score > 0) matchCount++;
            }
          });
        });
        
        // Require at least one match for inclusion
        if (matchCount > 0) {
          return {
            ...r,
            searchScore: totalScore / (searchTerms.length * 100), // Normalize score
            matchCount: matchCount
          };
        }
        return null;
      })
      .filter(r => r !== null)
      .sort((a, b) => {
        // Sort by search score first, then by rating
        const scoreDiff = b.searchScore - a.searchScore;
        if (Math.abs(scoreDiff) > 0.1) return scoreDiff;
        return (b.rating || 0) - (a.rating || 0);
      });
    }
    
    // Apply other filters
    if (params.city) {
      filtered = filtered.filter(r => 
        r.city.toLowerCase().includes(params.city.toLowerCase())
      );
    }
    
    if (params.certification) {
      filtered = filtered.filter(r => 
        r.certifications.some(cert => 
          cert.abbreviation.toLowerCase() === params.certification.toLowerCase()
        )
      );
    }
    
    if (params.cuisine) {
      filtered = filtered.filter(r => 
        r.cuisine_types.some(type => 
          type.toLowerCase().includes(params.cuisine.toLowerCase())
        )
      );
    }
    
    if (params.price_range) {
      filtered = filtered.filter(r => r.price_range === parseInt(params.price_range));
    }
    
    // Pagination
    const page = parseInt(params.page) || 1;
    const limit = parseInt(params.limit) || 20;
    const offset = (page - 1) * limit;
    const paginated = filtered.slice(offset, offset + limit);
    
    return {
      data: {
        restaurants: paginated,
        pagination: {
          page,
          limit,
          total: filtered.length,
          totalPages: Math.ceil(filtered.length / limit),
          hasNext: page < Math.ceil(filtered.length / limit),
          hasPrev: page > 1
        },
        filters: {
          cities: [...new Set(restaurants.map(r => r.city))].sort(),
          certifications: [...new Set(restaurants.flatMap(r => r.certifications.map(c => c.abbreviation)))].sort(),
          cuisines: [...new Set(restaurants.flatMap(r => r.cuisine_types))].sort(),
          states: [...new Set(restaurants.map(r => r.state))].sort()
        }
      }
    };
  },

  suggestions: async (query) => {
    const data = await loadRestaurantData();
    const restaurants = transformRestaurantData(data);
    
    if (!query || query.length < 2) {
      return { data: { suggestions: [] } };
    }
    
    const queryLower = query.toLowerCase();
    const suggestions = [];
    
    // Fuzzy scoring function
    const fuzzyScore = (text, search) => {
      if (!text || !search) return 0;
      const textLower = text.toLowerCase();
      
      // Exact match
      if (textLower === search) return 100;
      // Starts with
      if (textLower.startsWith(search)) return 90;
      // Contains
      if (textLower.includes(search)) return 80;
      
      // Character-by-character matching
      let score = 0;
      let searchIndex = 0;
      for (let i = 0; i < textLower.length && searchIndex < search.length; i++) {
        if (textLower[i] === search[searchIndex]) {
          score += 1;
          searchIndex++;
        }
      }
      
      return (score / search.length) * 60;
    };
    
    // Restaurant suggestions
    restaurants.forEach(r => {
      const nameScore = fuzzyScore(r.name, queryLower);
      if (nameScore > 50) {
        suggestions.push({
          text: r.name,
          type: 'restaurant',
          id: r.id,
          score: nameScore,
          location: `${r.city}, ${r.state}`,
          restaurant: r
        });
      }
    });
    
    // Cuisine suggestions
    const cuisines = [...new Set(restaurants.map(r => r.cuisine_types[0] || r.cuisine_type).filter(Boolean))];
    cuisines.forEach(cuisine => {
      const score = fuzzyScore(cuisine, queryLower);
      if (score > 50) {
        const count = restaurants.filter(r => 
          r.cuisine_types.includes(cuisine) || r.cuisine_type === cuisine
        ).length;
        suggestions.push({
          text: cuisine,
          type: 'cuisine',
          score: score,
          count: count
        });
      }
    });
    
    // City suggestions
    const cities = [...new Set(restaurants.map(r => `${r.city}, ${r.state}`).filter(Boolean))];
    cities.forEach(city => {
      const score = fuzzyScore(city, queryLower);
      if (score > 50) {
        const count = restaurants.filter(r => `${r.city}, ${r.state}` === city).length;
        suggestions.push({
          text: city,
          type: 'location',
          score: score,
          count: count
        });
      }
    });
    
    // Certification suggestions
    const certifications = [...new Set(restaurants.map(r => r.certifications.map(c => c.abbreviation)).flat().filter(Boolean))];
    certifications.forEach(cert => {
      const score = fuzzyScore(cert, queryLower);
      if (score > 50) {
        const count = restaurants.filter(r => 
          r.certifications.some(c => c.abbreviation === cert)
        ).length;
        suggestions.push({
          text: cert,
          type: 'certification',
          score: score,
          count: count
        });
      }
    });
    
    return { 
      data: { 
        suggestions: suggestions
          .sort((a, b) => b.score - a.score)
          .slice(0, 8)
      } 
    };
  },

  filters: async () => {
    const data = await loadRestaurantData();
    const restaurants = transformRestaurantData(data);
    
    return {
      data: {
        cities: [...new Set(restaurants.map(r => r.city))],
        certifications: [...new Set(restaurants.flatMap(r => 
          r.certifications.map(c => ({ abbreviation: c.abbreviation, name: c.name }))
        ))],
        cuisines: [...new Set(restaurants.flatMap(r => r.cuisine_types))],
        priceRanges: [
          { value: 1, label: '$' },
          { value: 2, label: '$$' },
          { value: 3, label: '$$$' },
          { value: 4, label: '$$$$' }
        ]
      }
    };
  }
};

export const restaurantAPI = {
  getById: async (id) => {
    const data = await loadRestaurantData();
    const restaurants = transformRestaurantData(data);
    const restaurant = restaurants.find(r => r.id === parseInt(id));
    
    if (!restaurant) {
      throw new Error('Restaurant not found');
    }
    
    return { data: { restaurant } };
  },

  getReviews: async (id, params = {}) => {
    // For MVP, return empty reviews
    return {
      data: {
        reviews: [],
        pagination: { page: 1, limit: 10, total: 0, totalPages: 0 }
      }
    };
  },

  submitCorrection: async (id, data) => {
    // For MVP, just return success
    return { data: { message: 'Correction submitted successfully' } };
  }
};

export const certificationAPI = {
  getAll: async () => {
    const data = await loadRestaurantData();
    const restaurants = transformRestaurantData(data);
    
    const certifications = [...new Set(restaurants.flatMap(r => 
      r.certifications.map(c => ({ 
        abbreviation: c.abbreviation, 
        name: c.name,
        description: `Kosher certification by ${c.name}`,
        website: '',
        restaurant_count: restaurants.filter(rest => 
          rest.certifications.some(cert => cert.abbreviation === c.abbreviation)
        ).length
      }))
    ))];
    
    return { data: { certifications } };
  },

  getByAbbreviation: async (abbr) => {
    const allCerts = await certificationAPI.getAll();
    const cert = allCerts.data.certifications.find(c => 
      c.abbreviation.toLowerCase() === abbr.toLowerCase()
    );
    
    if (!cert) {
      throw new Error('Certification not found');
    }
    
    return { data: { certification: cert } };
  },

  getRestaurants: async (abbr, params = {}) => {
    return searchAPI.search({ ...params, certification: abbr });
  }
};

export const submissionAPI = {
  create: async (data) => {
    // For MVP, just return success
    return { 
      data: { 
        id: Math.random().toString(36).substr(2, 9),
        status: 'pending',
        message: 'Submission received successfully' 
      } 
    };
  },

  getStatus: async (id) => {
    return { 
      data: { 
        id, 
        status: 'pending',
        message: 'Submission is being reviewed' 
      } 
    };
  },

  bulk: async (data) => {
    return { 
      data: { 
        processed: data.restaurants?.length || 0,
        message: 'Bulk submission processed successfully' 
      } 
    };
  }
};

// Utility functions (same as original api.js)
export const utils = {
  buildSearchQuery: (filters) => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, v));
        } else {
          params.append(key, value);
        }
      }
    });
    
    return params.toString();
  },

  formatRestaurant: (restaurant) => ({
    ...restaurant,
    formattedAddress: `${restaurant.street_address}, ${restaurant.city}, ${restaurant.state} ${restaurant.zip_code}`.replace(/^, |, $/, ''),
    priceRangeText: getPriceRangeText(restaurant.price_range),
    certificationsList: restaurant.certifications?.map(cert => cert.abbreviation).join(', ') || 'None listed',
    dietaryList: restaurant.dietary_requirements?.join(', ') || 'Standard kosher',
  }),

  formatReview: (review) => ({
    ...review,
    formattedDate: new Date(review.review_date).toLocaleDateString(),
    excerpt: review.content?.length > 150 
      ? review.content.substring(0, 150) + '...' 
      : review.content,
  }),

  isRestaurantOpen: (hours, isShabbos = false) => {
    if (isShabbos && hours?.shabbos_hours) {
      return false;
    }
    return true;
  },
};

function getPriceRangeText(priceRange) {
  switch (priceRange) {
    case 1: return '$';
    case 2: return '$$';
    case 3: return '$$$';
    case 4: return '$$$$';
    default: return '';
  }
}

export default {
  searchAPI,
  restaurantAPI,
  certificationAPI,
  submissionAPI,
  utils
};